import { FlaskConical, Users, ListChecks, ShieldAlert, Quote } from "lucide-react";

// Plain-English explainer for the drug-testing consortium / C-TPA (guide Step/Mission 8).
export default function DrugConsortiumExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="drug-consortium-explainer">
      <div className="flex items-center gap-2 mb-3">
        <FlaskConical className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">Drug testing &amp; the consortium</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Before anyone — including you — drives, federal law requires a passing <strong>pre-employment drug test</strong>
        {" "}and enrollment in a <strong>random testing program</strong>. For a small carrier, a consortium handles all
        of it.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What is a C/TPA (consortium)?</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            A Consortium/Third-Party Administrator runs your DOT drug &amp; alcohol program: pre-employment tests, a
            <strong> random-testing pool</strong>, collection sites, lab results and Clearinghouse reporting. A one-truck
            operator can't run their own random pool — you must join one.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What's required &amp; when</span>
          </div>
          <ul className="text-sm text-slate-700 leading-relaxed list-disc pl-4 space-y-1">
            <li>Verified negative pre-employment test before dispatch</li>
            <li>Stay in the random pool all year</li>
            <li>Post-accident &amp; reasonable-suspicion testing</li>
            <li>Results reported through the Clearinghouse</li>
          </ul>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          Putting yourself or a driver on the road before a <strong>verified negative test</strong> and random-pool
          enrollment is a serious federal violation — an instant out-of-service order and fines big enough to end a
          startup before its first profitable month.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Join a reputable consortium early — it's inexpensive and they walk you through every test. Keep your proof of
          enrollment on file for the safety audit. This isn't legal advice.
        </p>
      </div>
    </div>
  );
}
